/**
 * Automatic leader-label placement.
 *
 * A deterministic greedy optimiser with a refinement pass. For every label that
 * is not pinned by the user it evaluates candidate boxes around the anchor
 * (16 directions × 4 distances) and picks the lowest-cost one. Costs encode the
 * cartographic conventions used by professional plan markups:
 *   • never cover another label, a marker, or a highlighted area
 *   • avoid leaders crossing other leaders, labels or markers
 *   • stay close, prefer upper-right, radiate outward from clusters
 *   • a label stays where it was unless that has become a poor place: when a test is added, the
 *     others hold still (the place it had is weighed first, staying costs nothing and moving costs
 *     a step, and the labels that had a place are placed before the new ones)
 */
import type { Vec } from '../../shared/doc.ts';
import { add, dist, mul, norm, overlapArea, segmentIntersectsRect, segmentsIntersect, distToSegment, sub, type Rect } from './geom.ts';
import { exits } from './soils.ts';

export interface LeaderGeom {
  pts: Vec[]; // polyline from label edge to tip (tip last)
  tip: Vec;
  dir: Vec; // unit direction of the final segment
}

/**
 * Leader from a label rect to a target, as the soils leaders are drawn (soils.ts): out of the
 * centre of the edge that faces the target best, by a short stub straight out of it, then straight
 * to the target (stopping `tipInset` short of it).
 */
export function leaderGeometry(rect: Rect, target: Vec, _radius: number, landing: number, tipInset: number, sides?: 'lr'): LeaderGeom {
  const [start, bend] = exits(rect, target, { landing, ...(sides ? { sides } : {}) })[0]!.head as [Vec, Vec];
  const d = norm(sub(target, bend));
  const tip = add(target, mul(d, -tipInset));
  return { pts: [start, bend, tip], tip, dir: d };
}

function leaderSegments(g: LeaderGeom): [Vec, Vec][] {
  const out: [Vec, Vec][] = [];
  for (let i = 0; i < g.pts.length - 1; i++) out.push([g.pts[i]!, g.pts[i + 1]!]);
  return out;
}
function leadersCross(a: LeaderGeom, b: LeaderGeom): boolean {
  for (const [p, q] of leaderSegments(a)) for (const [r, s] of leaderSegments(b)) if (segmentsIntersect(p, q, r, s)) return true;
  return false;
}
function leaderHitsRect(g: LeaderGeom, r: Rect): boolean {
  for (const [p, q] of leaderSegments(g)) if (segmentIntersectsRect(p, q, r)) return true;
  return false;
}
function leaderNear(g: LeaderGeom, p: Vec, d: number): boolean {
  for (const [a, b] of leaderSegments(g)) if (distToSegment(p, a, b) < d) return true;
  return false;
}

export interface LabelReq {
  id: string;
  anchor: Vec;
  w: number;
  h: number;
  pinned: Vec | null; // user-chosen top-left
  keepOut: number; // clearance radius around the anchor (marker radius + gap)
  hint: Vec | null; // preferred unit direction (e.g. away from a cluster)
  spread: number; // how far a cluster extends in the hint direction (0 for none)
  priority: number; // lower places first
  /** Pinned, but only passing (a label carried where it may not rest): nothing else keeps clear of it. */
  ghost?: boolean;
  /** Its leader leaves by the centre of its left or right side only (an elevation tag's). */
  sides?: 'lr';
}

export interface Obstacle {
  rect: Rect;
  weight: number; // 1 = mild, 5 = severe
  /** The pieces of one thing (the cells of one area's body): a label on several of them is on it once, by as much as it covers of them all. */
  group?: string;
  /**
   * Kept off only by a label placed afresh: one that already has its place keeps it. (The plan's
   * lettering, read a moment after the map opens: a label already on screen never moves for it.)
   */
  fresh?: boolean;
}

export interface LayoutInput {
  bounds: Rect;
  unit: number; // base size unit (image px)
  radius: number; // label corner radius (for leader attachment)
  landing: number; // horizontal landing length
  labels: LabelReq[];
  obstacles: Obstacle[];
  prev: ReadonlyMap<string, Rect>; // previous auto placements (hysteresis)
  /** Leaders already on the plan (callouts' arrows): a label's leader crosses one only if it must. */
  lines?: readonly (readonly Vec[])[];
  /** Where no label may be at all, as if off the plan (the legend's box). */
  keepOff?: readonly Rect[];
  /** Look farther out too (a crowd of labels round one place needs more rings than the usual reach holds); `farther`: farther still (a heap of tests). */
  far?: boolean;
  farther?: boolean;
}

export interface Placement {
  id: string;
  rect: Rect;
  auto: boolean;
}

const DIRS: Vec[] = [];
for (let i = 0; i < 16; i++) {
  const a = (i / 16) * Math.PI * 2;
  DIRS.push({ x: Math.cos(a), y: -Math.sin(a) }); // screen coords: y down, angle 0 = right, 90 = up
}

/**
 * The directions to try at a distance: the sixteen near by, and as many more farther out as keep
 * the places tried no farther apart round the ring than `spacing` (a crowd's outer rings need them:
 * sixteen places round a wide ring leave most of it untried). At most sixty-four.
 */
const RINGS = new Map<number, Vec[]>();
function dirsAt(radius: number, spacing: number): Vec[] {
  const n = Math.min(64, Math.max(16, 8 * Math.ceil((2 * Math.PI * radius) / spacing / 8)));
  if (n === 16) return DIRS;
  let ring = RINGS.get(n);
  if (!ring) {
    ring = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      ring.push({ x: Math.cos(a), y: -Math.sin(a) });
    }
    RINGS.set(n, ring);
  }
  return ring;
}

/** Cartographic direction preference: upper-right best, then upper-left, lower-right, lower-left, right, left, up, down. */
function dirPenalty(d: Vec): number {
  const ang = Math.atan2(-d.y, d.x); // up positive
  const deg = ((ang * 180) / Math.PI + 360) % 360;
  // Distances from ideal angles.
  const prefs: [number, number][] = [
    [30, 0],
    [150, 0.35],
    [330, 0.7],
    [210, 1.0],
    [0, 1.2],
    [180, 1.4],
    [90, 1.6],
    [270, 1.9],
  ];
  let best = Infinity;
  for (const [a, p] of prefs) {
    const diff = Math.min(Math.abs(deg - a), 360 - Math.abs(deg - a));
    const cost = p + diff / 90;
    if (cost < best) best = cost;
  }
  return best;
}

function centre(r: Rect): Vec {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

function keepOutRect(a: Vec, r: number): Rect {
  return { x: a.x - r, y: a.y - r, w: 2 * r, h: 2 * r };
}

interface PlacedInfo {
  id: string;
  rect: Rect;
  leader: LeaderGeom;
  /** The box round its leader (what cannot meet it cannot cross it). */
  lbox: Rect;
  anchor: Vec;
  keepOut: number;
  /** The box round its label, its leader and its anchor's keep-out: nothing wholly outside it can touch any of them. */
  reach: Rect;
}

/** The box round a label, its leader and its anchor (with its keep-out). */
function reachOf(rect: Rect, anchor: Vec, keepOut: number): Rect {
  const x0 = Math.min(rect.x, anchor.x - keepOut),
    y0 = Math.min(rect.y, anchor.y - keepOut);
  return { x: x0, y: y0, w: Math.max(rect.x + rect.w, anchor.x + keepOut) - x0, h: Math.max(rect.y + rect.h, anchor.y + keepOut) - y0 };
}
function boxesApart(a: Rect, b: Rect): boolean {
  return a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y;
}
/** The box round a polyline. */
function boxOf(pts: readonly Vec[]): Rect {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of pts) {
    if (p.x < x0) x0 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.x > x1) x1 = p.x;
    if (p.y > y1) y1 = p.y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
/** Whether a point lies within `d` of a box. */
function nearBox(p: Vec, b: Rect, d: number): boolean {
  return p.x >= b.x - d && p.x <= b.x + b.w + d && p.y >= b.y - d && p.y <= b.y + b.h + d;
}

export function layoutLabels(input: LayoutInput): Placement[] {
  const { bounds, unit, radius, landing } = input;
  const leaderFor = (rect: Rect, anchor: Vec, keepOut: number, sides?: 'lr'): LeaderGeom => leaderGeometry(rect, anchor, radius, landing, keepOut, sides);
  const margin = 2 * unit;
  const apart = 3 * unit;
  const placed: PlacedInfo[] = [];
  const result: Placement[] = [];
  const byId = new Map<string, PlacedInfo>();

  // the labels that had a place first (they keep it if they may), then the new ones, each in its order
  const labels = input.labels.slice().sort((a, b) => (input.prev.has(a.id) ? 0 : 1) - (input.prev.has(b.id) ? 0 : 1) || a.priority - b.priority);

  // 1. Pinned labels are fixed obstacles.
  for (const l of labels) {
    if (!l.pinned) continue;
    const rect = { x: l.pinned.x, y: l.pinned.y, w: l.w, h: l.h };
    const leader = leaderFor(rect, l.anchor, l.keepOut, l.sides);
    const info: PlacedInfo = { id: l.id, rect, leader, lbox: boxOf(leader.pts), anchor: l.anchor, keepOut: l.keepOut, reach: reachOf(rect, l.anchor, l.keepOut) };
    if (!l.ghost) placed.push(info);
    byId.set(l.id, info);
    result.push({ id: l.id, rect, auto: false });
  }

  const autos = labels.filter((l) => !l.pinned);
  // the callouts' arrows, with the boxes round them
  const arrows = (input.lines ?? []).map((l) => ({ g: { pts: l as Vec[], tip: l[l.length - 1]!, dir: { x: 0, y: 0 } }, box: boxOf(l) }));

  const keepOff = input.keepOff ?? [];
  const outside = (rect: Rect) => rect.x < bounds.x + margin || rect.y < bounds.y + margin || rect.x + rect.w > bounds.x + bounds.w - margin || rect.y + rect.h > bounds.y + bounds.h - margin || keepOff.some((k) => overlapArea(rect, k) > 0);

  /** What a place costs whatever is round it: how far, which way, and whether the label was there before. */
  const plainCost = (l: LabelReq, rect: Rect, dir: Vec, d: number): number => {
    let cost = 0;
    // Distance & direction
    cost += (d / unit) * 0.2;
    cost += dirPenalty(dir) * 1.0;
    if (l.hint) {
      const align = dir.x * l.hint.x + dir.y * l.hint.y; // -1..1
      cost += (1 - align) * 7;
    }
    // Where it was: staying costs nothing, moving at all a step, and more the farther (by its middle, so a label grown or shrunk stays put)
    const prev = input.prev.get(l.id);
    if (prev) {
      const moved = dist(centre(prev), centre(rect));
      if (moved >= 0.5 * unit) cost += 8 + Math.min(12, moved / (8 * unit));
    }
    return cost;
  };

  /**
   * What a place costs for what is round it and the way its leader runs. Once it costs `bound` it can
   * be no one's choice: it says so then and looks no further (most places tried, in a crowd, are
   * soon out of the running).
   */
  const roundCost = (l: LabelReq, rect: Rect, leader: LeaderGeom, exclude: string, obstacles: readonly Obstacle[], bound: number): number => {
    const area = rect.w * rect.h || 1;
    const lbox = boxOf(leader.pts);
    // a leader runs on out of the centre of its label's edge, or turns off its stub as a shoulder does; one that must hook round, square off it or worse, reads badly
    const [s0, s1, s2] = leader.pts as [Vec, Vec, Vec];
    const n = norm(sub(s1, s0)),
      r = norm(sub(s2, s1));
    let cost = Math.max(0, 0.5 - (n.x * r.x + n.y * r.y)) * 12;
    // one that must turn back past the edge it left by (a badge right over its dot, its leader out of a side) is a poor place
    if (n.x * r.x + n.y * r.y < 0) cost += 30;
    // Obstacles
    let groups: Set<string> | null = null;
    for (const o of obstacles) {
      const ov = overlapArea(rect, o.rect);
      if (ov > 0) {
        // the pieces of one thing cost as that one thing: once, by how much of the label they cover
        if (o.group === undefined) cost += o.weight * 3;
        else if (!(groups ??= new Set()).has(o.group)) {
          groups.add(o.group);
          cost += o.weight * 3;
        }
        cost += o.weight * (ov / area) * 12;
      }
      // leaders may cross areas (mild), never a box
      if (o.weight >= 3 && !boxesApart(lbox, o.rect) && leaderHitsRect(leader, o.rect)) cost += o.weight >= 5 ? 30 : 2;
    }
    if (cost >= bound) return cost;
    // the callouts' arrows stand where they are (a test never moves one): a label keeps off them, and its leader does not cross them
    for (const a of arrows) {
      if (!boxesApart(lbox, a.box) && leadersCross(leader, a.g)) cost += 40;
      if (!boxesApart(rect, a.box) && leaderHitsRect(a.g, rect)) cost += 45;
    }
    if (cost >= bound) return cost;
    // Other labels / leaders / anchors (the ones whose reach comes nowhere near cannot touch: most of them, on a map of many tests)
    const reach = reachOf(rect, l.anchor, l.keepOut);
    for (const p of placed) {
      if (p.id === exclude || boxesApart(reach, p.reach)) continue;
      // (two labels stand apart by at least a hair more than their white casings: never edge to edge)
      const ov = overlapArea(rect, { x: p.rect.x - apart, y: p.rect.y - apart, w: p.rect.w + 2 * apart, h: p.rect.h + 2 * apart });
      if (ov > 0) cost += 300 + (ov / area) * 200;
      if (!boxesApart(lbox, p.rect) && leaderHitsRect(leader, p.rect)) cost += 30;
      if (!boxesApart(lbox, p.lbox) && leadersCross(leader, p.leader)) cost += 40;
      if (!boxesApart(p.lbox, rect) && leaderHitsRect(p.leader, rect)) cost += 30;
      const ko = keepOutRect(p.anchor, p.keepOut);
      if (overlapArea(rect, ko) > 0) cost += 150;
      if (nearBox(p.anchor, lbox, p.keepOut) && leaderNear(leader, p.anchor, p.keepOut)) cost += 22;
      if (cost >= bound) return cost;
    }
    // All anchors (including not-yet-placed) must never be covered by a label.
    for (const o of labels) {
      if (o.id === l.id || byId.has(o.id)) continue;
      if (overlapArea(rect, keepOutRect(o.anchor, o.keepOut)) > 0) cost += 150;
      if (nearBox(o.anchor, lbox, o.keepOut) && leaderNear(leader, o.anchor, o.keepOut)) cost += 22;
    }
    return cost;
  };

  const place = (l: LabelReq, exclude: string): PlacedInfo => {
    const d0 = l.keepOut + 5 * unit;
    const dists =
      l.spread > 0
        ? [d0, d0 + l.spread * 0.5, d0 + l.spread, d0 + l.spread * 1.6, d0 + l.spread * 2.4, d0 + l.spread * 2.4 + 40 * unit, d0 + l.spread * 2.4 + 80 * unit]
        : [d0, d0 + 8 * unit, d0 + 18 * unit, d0 + 32 * unit, d0 + 55 * unit, d0 + 85 * unit, d0 + 125 * unit];
    if (input.far || input.farther) {
      const last = dists[dists.length - 1]!;
      dists.push(last + 45 * unit, last + 95 * unit, last + 150 * unit, last + 215 * unit);
      if (input.farther) dists.push(last + 285 * unit, last + 360 * unit, last + 440 * unit, last + 530 * unit, last + 630 * unit);
    }
    // only what lies within its farthest reach can touch it (on a plan with much lettering, most of it is far off)
    const out = dists[dists.length - 1]! + Math.max(l.w, l.h) + l.keepOut + landing;
    const zone = { x: l.anchor.x - out, y: l.anchor.y - out, w: 2 * out, h: 2 * out };
    const had = input.prev.has(l.id);
    const obstacles = input.obstacles.filter((o) => !boxesApart(o.rect, zone) && !(o.fresh && had));
    let best: { cost: number; rect: Rect; leader: LeaderGeom } | null = null;
    const consider = (rect: Rect, dir: Vec, d: number) => {
      if (outside(rect)) return;
      const plain = plainCost(l, rect, dir, d);
      if (best && plain >= best.cost) return;
      const leader = leaderFor(rect, l.anchor, l.keepOut, l.sides);
      const cost = plain + roundCost(l, rect, leader, exclude, obstacles, best ? best.cost - plain : Infinity);
      if (!best || cost < best.cost) best = { cost, rect, leader };
    };
    // where it was, first: kept when nothing has come to make it a poor place
    const was = input.prev.get(l.id);
    if (was) {
      const c = centre(was);
      const off = sub(c, l.anchor);
      const len = Math.hypot(off.x, off.y);
      if (len > 1e-6) {
        const dir = mul(off, 1 / len);
        const extent = Math.abs(dir.x) * (l.w / 2) + Math.abs(dir.y) * (l.h / 2);
        consider({ x: c.x - l.w / 2, y: c.y - l.h / 2, w: l.w, h: l.h }, dir, Math.max(0, len - extent));
      }
    }
    for (const d of dists) {
      for (const dir of dirsAt(d + 0.5 * (l.w + l.h) / 2, 0.6 * ((l.w + l.h) / 2))) {
        const extent = Math.abs(dir.x) * (l.w / 2) + Math.abs(dir.y) * (l.h / 2);
        const c = add(l.anchor, mul(dir, d + extent));
        consider({ x: c.x - l.w / 2, y: c.y - l.h / 2, w: l.w, h: l.h }, dir, d);
      }
    }
    const found = best as { cost: number; rect: Rect; leader: LeaderGeom } | null;
    if (!found) {
      // Degenerate (label bigger than image): clamp near the anchor.
      const rect = {
        x: Math.max(bounds.x, Math.min(l.anchor.x + l.keepOut, bounds.x + bounds.w - l.w)),
        y: Math.max(bounds.y, Math.min(l.anchor.y - l.h - l.keepOut, bounds.y + bounds.h - l.h)),
        w: l.w,
        h: l.h,
      };
      const leader = leaderFor(rect, l.anchor, l.keepOut, l.sides);
      return { id: l.id, rect, leader, lbox: boxOf(leader.pts), anchor: l.anchor, keepOut: l.keepOut, reach: reachOf(rect, l.anchor, l.keepOut) };
    }
    return { id: l.id, rect: found.rect, leader: found.leader, lbox: boxOf(found.leader.pts), anchor: l.anchor, keepOut: l.keepOut, reach: reachOf(found.rect, l.anchor, l.keepOut) };
  };

  // 2. Greedy pass.
  for (const l of autos) {
    const info = place(l, '');
    placed.push(info);
    byId.set(l.id, info);
  }
  // 3. Refinement passes: re-place each label given all the others.
  for (let pass = 0; pass < 2; pass++) {
    for (const l of autos) {
      const cur = byId.get(l.id)!;
      const idx = placed.indexOf(cur);
      const info = place(l, l.id);
      placed[idx] = info;
      byId.set(l.id, info);
    }
  }
  for (const l of autos) result.push({ id: l.id, rect: byId.get(l.id)!.rect, auto: true });
  return result;
}

/**
 * Cluster hints: for each point, a unit vector pointing away from the centroid
 * of the points within `r`, and the cluster's extent in that direction.
 */
export function clusterHints(points: { id: string; p: Vec }[], r: number): Map<string, { hint: Vec | null; spread: number }> {
  const out = new Map<string, { hint: Vec | null; spread: number }>();
  for (const a of points) {
    const near = points.filter((b) => dist(a.p, b.p) <= r);
    if (near.length < 2) {
      out.set(a.id, { hint: null, spread: 0 });
      continue;
    }
    let cx = 0,
      cy = 0;
    for (const b of near) {
      cx += b.p.x;
      cy += b.p.y;
    }
    const c = { x: cx / near.length, y: cy / near.length };
    const dv = sub(a.p, c);
    const hint = dist(a.p, c) < 1e-6 ? null : norm(dv);
    // Extent: farthest neighbour projected onto the hint direction beyond this point.
    let spread = 0;
    if (hint) {
      for (const b of near) {
        const proj = (b.p.x - a.p.x) * hint.x + (b.p.y - a.p.y) * hint.y;
        if (proj > spread) spread = proj;
      }
      // Also account for the cluster's radius so labels clear the whole group.
      let rad = 0;
      for (const b of near) rad = Math.max(rad, dist(b.p, c));
      spread = Math.max(spread, rad - dist(a.p, c));
    }
    out.set(a.id, { hint, spread });
  }
  return out;
}
